/**
 * Hook for managing Kanban board state using Apollo-generated hooks
 * @remarks
 * This hook provides declarative board management using Apollo Client hooks instead
 * of imperative service calls. Key features:
 * - Reactive data fetching with useQuery hooks
 * - Optimistic UI updates for instant feedback
 * - Automatic cache management and rollback
 * - Proper error handling via onError callbacks
 * - Zero console pollution
 * - Apollo loading states to prevent duplicate phase creation during rapid re-renders
 *
 * This block used to claim Apollo loading states "persist across component
 * mounts, unlike refs". They do not — a mutation's `loading` belongs to the
 * mount that issued it, and a remount starts clean. Believing otherwise is what
 * let the default-board create fire again on every later mount (SE-6796). Board
 * creation is now guarded by an identity claim in
 * `shared/defaultBoardCreationRegistry`, which genuinely does outlive a mount.
 * @param boardId - Optional board ID to load, if not provided loads first available board
 * @param defaultBoardName - Default name for new board creation
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  buildDefaultBoardIdentityKey,
  hasAttemptedDefaultBoard,
  markDefaultBoardAttempted,
  releaseDefaultBoardAttempt,
} from "./shared/defaultBoardCreationRegistry";
import {
  DEFAULT_PHASE_NAMES,
  generateClosedGroups,
  generateColumnsData,
  generateUnstagedColumnData,
} from "./shared/kanbanBoardUtils";
import useKanbanBoardMutations from "./useKanbanBoardMutations";
import useKanbanBoardQueries from "./useKanbanBoardQueries";
import useKanbanCardOperations from "./useKanbanCardOperations";
import useKanbanPhaseOperations from "./useKanbanPhaseOperations";
import useStageMovePrompt from "./useStageMovePrompt";

import { KanbanSourceType } from "@/generated/graphql";
import { useToastNotification } from "@/hooks/useToastNotification";

export /**
 *
 */
const useKanbanBoard = (
  boardId?: string | null,
  defaultBoardName: string = "Player Pipeline"
) => {
  // Local state for error handling and saving status
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Toast for non-critical operation errors (add/delete column, move player)
  const toast = useToastNotification();
  const handleOperationError = useCallback(
    (message: string) => toast.show({ action: "danger", description: message }),
    [toast]
  );

  // Extracted query logic
  const queries = useKanbanBoardQueries(boardId, setError);

  // Extracted mutation logic — critical errors (board creation) use setError (full-screen),
  // operation errors use handleOperationError (toast)
  const mutations = useKanbanBoardMutations(
    queries.board,
    queries.phases,
    setError,
    handleOperationError
  );

  // Phase operations
  const phaseOps = useKanbanPhaseOperations({
    board: queries.board,
    phases: queries.phases,
    cards: queries.cards,
    mutations,
    refetchPhases: queries.refetchPhases,
    setError,
    setIsSaving,
  });

  // Pending moves: locks a card in its destination column at the React level
  // so that Apollo's multi-broadcast optimistic-layer removal never causes a
  // visible snap-back flicker (SE-4272).
  //
  // onMoveStart is called synchronously (inside flushSync) and adds an entry.
  // On success the entry is kept — Apollo emits late ghost broadcasts where
  // the optimistic layer is removed in one broadcast and the server response
  // is written in the next, briefly revealing the source-phase state. The
  // override masks that window. It is replaced on the next drag of the same
  // card, so entries never leak.
  //
  // On error the entry MUST be released so Apollo's rollback becomes visible.
  //
  // This comment used to say the mutation's `catch` path does that, and that
  // was wrong (SE-6278). `moveCardMutation` carries a hook-level `onError`, so
  // Apollo RESOLVES rather than rejects and the `catch` never runs for a failed
  // move — the override survived and kept the card displayed in a stage it had
  // never reached, on top of a cache that had already rolled back. The release
  // is now driven by the absent-data check in `movePlayerToPhase`, alongside
  // the `catch` (which still covers genuinely thrown errors).
  //
  // Entries carry a moveId so a rapid second drag's entry is never wiped by
  // the first drag's deferred cleanup.
  const [pendingMoves, setPendingMoves] = useState<
    Map<string, { toPhaseId: string; phaseName: string; moveId: string }>
  >(new Map());

  const onMoveStart = useCallback(
    (cardId: string, toPhaseId: string, phaseName: string, moveId: string) => {
      setPendingMoves(prev =>
        new Map(prev).set(cardId, { toPhaseId, phaseName, moveId })
      );
    },
    []
  );

  const onMoveEnd = useCallback((cardId: string, moveId: string) => {
    setPendingMoves(prev => {
      const existing = prev.get(cardId);
      // moveId scoping: a rapid second drag of the same card replaces the
      // entry with a new moveId. When the first drag's cleanup runs, its
      // stale moveId no longer matches, so it must not wipe the second's
      // entry.
      if (!existing || existing.moveId !== moveId) return prev;
      return new Map([...prev].filter(([key]) => key !== cardId));
    });
  }, []);

  // Pending ranks: the same-phase rank-reorder analogue of pendingMoves
  // (SE-4687). A within-column reorder does not change kanbanPhaseId, so it
  // needs its own override keyed on `position`. onRankStart is called
  // synchronously (inside flushSync) so a reorder lands instantly while its
  // network save is debounced; the override holds the displayed order across
  // the debounce window and until the save settles, then is cleared by
  // onRankEnd. Entries carry a batchId so a newer window's override is never
  // wiped by an older, still-in-flight save's cleanup.
  const [pendingRanks, setPendingRanks] = useState<
    Map<string, { position: number; batchId: string }>
  >(new Map());

  const onRankStart = useCallback(
    (entries: { cardId: string; position: number }[], batchId: string) => {
      setPendingRanks(prev => {
        // Build immutably: the Map constructor lets later duplicate keys win,
        // so the new entries override any prior override for the same cards.
        const additions = entries.map(
          ({ cardId, position }) => [cardId, { position, batchId }] as const
        );
        return new Map<string, { position: number; batchId: string }>([
          ...prev,
          ...additions,
        ]);
      });
    },
    []
  );

  const onRankEnd = useCallback((batchId: string) => {
    setPendingRanks(prev => {
      // batchId scoping: only drop entries this save owns; a newer window that
      // re-pinned the same cards under a fresh batchId survives.
      const remaining = [...prev].filter(
        ([, value]) => value.batchId !== batchId
      );
      return remaining.length === prev.size ? prev : new Map(remaining);
    });
  }, []);

  // SE-5495: the sensitive-stage reason prompt. Owned here, one level above
  // the card operations, so EVERY move that funnels through
  // `movePlayerToPhase` is gated by construction — board drag today, and the
  // player drawer / profile entry points (SE-5494 and its sibling) the moment
  // they route through this hook. There is no second, ungated move path to
  // forget about.
  const stageMovePrompt = useStageMovePrompt();

  // Card operations
  const cardOps = useKanbanCardOperations({
    board: queries.board,
    phases: queries.phases,
    cards: queries.cards,
    mutations,
    refetchCards: queries.refetchCards,
    setError,
    setIsSaving,
    onMoveStart,
    onMoveEnd,
    onRankStart,
    onRankEnd,
    requestStageMoveDecision: stageMovePrompt.requestStageMoveDecision,
  });

  // Destructure for convenience
  const { board, phases, cards } = queries;

  // Convert to ColumnData format for UI.
  // When a move is pending, override the card's phase so the UI never shows
  // it in the source column during Apollo's optimistic-layer rollback. When a
  // rank reorder is pending, override the card's ordering so the reordered
  // sequence renders instantly while its save is debounced (SE-4687).
  //
  // SE-5493: BOTH ordering keys are overridden, because generateColumnsData
  // sorts a PIPELINE board by the viewer's `rank` and every other board by the
  // shared `position`. Writing only `position` (the pre-SE-5493 behaviour)
  // would leave a pipeline drag sorted by the untouched server `rank`, so the
  // card would visibly snap back for the whole debounce window until the
  // refetch landed. `pendingRanks` stores the 0-based display index, which is
  // exactly what `position` wants and one less than the 1-based `rank`.
  const adjustedCards = useMemo(() => {
    const hasOverrides = pendingMoves.size > 0 || pendingRanks.size > 0;
    if (!hasOverrides) return cards;

    return cards.map(card => {
      const move = pendingMoves.get(card.id);
      const rank = pendingRanks.get(card.id);
      if (!move && !rank) return card;
      return {
        ...card,
        ...(rank ? { position: rank.position, rank: rank.position + 1 } : {}),
        ...(move
          ? {
              kanbanPhaseId: move.toPhaseId,
              kanbanPhase: {
                ...card.kanbanPhase,
                id: move.toPhaseId,
                name: move.phaseName,
              },
            }
          : {}),
      };
    });
  }, [cards, pendingMoves, pendingRanks]);

  // SE-5493: the board's source type selects the ordering key — PIPELINE reads
  // the current viewer's private `rank`, ROSTER/WATCHLIST the shared `position`.
  const boardSourceType = board?.sourceType;

  const columnsData = useMemo(
    () =>
      phases.length
        ? generateColumnsData(
            phases,
            adjustedCards,
            queries.organizationCurrency,
            boardSourceType
          )
        : [],
    [phases, adjustedCards, queries.organizationCurrency, boardSourceType]
  );

  // SE-5491 AC2: cards resolving to no rendered phase. The Kanban board never
  // renders this bucket (its columns come only from `phases`, so a "No stage"
  // column cannot appear); the Table view appends it so an orphan card is
  // surfaced instead of silently dropped by generateColumnsData's phase filter.
  // No `phases.length` guard: with zero rendered phases every card is orphaned,
  // which is exactly when the bucket matters most. generateUnstagedColumnData
  // already returns null when nothing is orphaned, so the empty-board case is
  // handled without dropping cards out of both renderers.
  const unstagedColumnData = useMemo(
    () =>
      generateUnstagedColumnData(
        phases,
        adjustedCards,
        queries.organizationCurrency
      ),
    [phases, adjustedCards, queries.organizationCurrency]
  );

  // SE-5491 AC1: the Table renderer's dataset. Same `columnsData` the Kanban
  // renders — identical stage membership, identical per-stage card order — plus
  // the defensive unstaged bucket. One derivation, two renderers; there is no
  // second query path.
  const tableColumnsData = useMemo(
    () =>
      unstagedColumnData ? [...columnsData, unstagedColumnData] : columnsData,
    [columnsData, unstagedColumnData]
  );

  // SE-5496: the Closed area's dataset — closed cards grouped by outcome in the
  // board template's `validOutcomes` order, appended at the END of the board.
  // Derived from the SAME `adjustedCards` as the columns, so a card can never
  // appear in both a stage column and the Closed area (generateColumnsData
  // excludes closed cards; this includes only them).
  const closedGroups = useMemo(
    () =>
      generateClosedGroups(
        adjustedCards,
        board?.validOutcomes ?? [],
        queries.organizationCurrency,
        boardSourceType
      ),
    [
      adjustedCards,
      board?.validOutcomes,
      queries.organizationCurrency,
      boardSourceType,
    ]
  );

  // SE-5496: reopen targets. A closed card may only be reopened into a WORKING
  // (non-terminal) stage — the terminal stages are excluded here so the reopen
  // picker can never offer one.
  const workingPhases = useMemo(
    () => phases.filter(phase => !phase.isTerminal),
    [phases]
  );

  // INITIALIZATION EFFECTS
  // Use Apollo's loading states instead of refs to prevent duplicate creation.
  // Loading states persist across component mounts (refs reset on each mount),
  // preventing the race condition where rapid navigation creates duplicate boards.
  //
  // The `!boardId` guard prevents auto-creation when we're trying to load
  // a specific board - this prevents infinite loops during org switch when
  // `boardId` points to a board from a different organization.
  const fetchedBoardId = board?.id;
  // SE-5487: PIPELINE boards own their stages via the creation template (the BE
  // seeds them) and the Stage Editor (SE-5488). A freshly-created CUSTOM
  // pipeline legitimately has ZERO phases, so the legacy default-phase auto-seed
  // below — a pre-pipeline behavior that back-fills DEFAULT_PHASE_NAMES onto any
  // empty board — must never fire for a PIPELINE board, or it would immediately
  // re-seed the 6 New-Acquisitions stages onto a board meant to start blank.
  const shouldCreatePhases =
    fetchedBoardId &&
    board?.sourceType !== KanbanSourceType.Pipeline &&
    !queries.phasesLoading &&
    phases.length === 0 &&
    !mutations.isCreatingPhase;
  // SE-6796: the identity a duplicate would collide on — the same tuple a
  // server-side uniqueness constraint would enforce. Null until every component
  // is known, which by itself blocks creation on a half-loaded session.
  const defaultBoardIdentityKey =
    !boardId && queries.currentUserId
      ? buildDefaultBoardIdentityKey(
          queries.organizationId,
          KanbanSourceType.Watchlist,
          queries.firstWatchlistId
        )
      : null;

  // SE-6796: auto-creation is INTENTIONAL — an account must always have at
  // least one board — so this condition is about creating exactly one, never
  // about creating none.
  //
  // Two changes make it sound:
  //   1. `queries.hasNoBoards` replaces `!board`. `!board` was a negative
  //      inference: a read that errored, was skipped, or had not settled
  //      produced no board and therefore looked identical to a genuinely empty
  //      account. `hasNoBoards` is positive evidence — the read succeeded and
  //      returned zero rows — so absence of data can no longer trigger a create.
  //   2. `hasAttemptedDefaultBoard` extends the guard ACROSS mounts.
  //      `isCreatingBoard` is Apollo mutation state and only spans the mount
  //      that owns it; a remount saw a clean slate and created a second board.
  const shouldCreateBoard =
    !boardId &&
    queries.hasNoBoards &&
    !!defaultBoardIdentityKey &&
    !mutations.isCreatingBoard &&
    !hasAttemptedDefaultBoard(defaultBoardIdentityKey);

  // Create board if none exists
  useEffect(() => {
    if (!shouldCreateBoard || !defaultBoardIdentityKey) {
      return;
    }
    // Claim the identity BEFORE issuing the mutation — a mount rendering in the
    // same tick must observe the claim, not the empty board list.
    markDefaultBoardAttempted(defaultBoardIdentityKey);
    // `createBoardMutation` carries a hook-level `onError`, so Apollo RESOLVES
    // with an absent `data` rather than rejecting on failure (same trap as
    // SE-6278). Both exits release the claim — bounded by the registry's
    // attempt cap — so one transient failure does not strand the account
    // without a board, and a persistent one cannot retry-storm.
    void Promise.resolve(
      mutations.createBoardMutation({
        variables: {
          input: {
            name: defaultBoardName,
            sourceId: queries.firstWatchlistId!,
            sourceType: KanbanSourceType.Watchlist,
          },
        },
      })
    )
      .then(result => {
        if (!result?.data?.createKanbanBoard) {
          releaseDefaultBoardAttempt(defaultBoardIdentityKey);
        }
      })
      .catch(() => {
        releaseDefaultBoardAttempt(defaultBoardIdentityKey);
      });
  }, [
    queries.firstWatchlistId,
    defaultBoardName,
    defaultBoardIdentityKey,
    mutations,
    shouldCreateBoard,
  ]);

  // Create default phases if board exists but has no phases
  // The isCreatingPhase check in shouldCreatePhases prevents duplicate creation
  useEffect(() => {
    if (shouldCreatePhases) {
      Promise.all(
        DEFAULT_PHASE_NAMES.map((name, index) =>
          mutations.createPhaseMutation({
            variables: {
              input: {
                kanbanBoardId: fetchedBoardId,
                name,
                position: index,
              },
            },
          })
        )
      );
    }
  }, [mutations, fetchedBoardId, shouldCreatePhases]);

  return {
    // State
    board,
    phases,
    cards,
    // SE-7624: the cards read's resolution state, so the screen can render an
    // explicit failed/stale treatment instead of an empty board when
    // `useListKanbanCardsQuery`'s bound error says the read did not succeed.
    cardsState: queries.cardsState,
    // SE-8534: the backend's own "you don't have access to this board"
    // message when the board identity read was denied, or null. The screen
    // reads this to render a non-retryable denial state instead of the
    // generic failed-read screen with a "Retry" button that would just
    // re-issue the same denied request.
    accessDeniedMessage: queries.accessDeniedMessage,
    columnsData,
    tableColumnsData,
    unstagedColumnData,
    isLoading: queries.isLoading,
    // SE-5630: the board whose content is on screen right now. The screen's
    // skeleton gate needs it to tell a cold start from a background refresh.
    loadedContentBoardId: queries.loadedContentBoardId,
    error,
    isSaving,

    // Roster board info
    isRosterBoard: cardOps.isRosterBoard,
    playerCount: cardOps.playerCount,

    // Actions - Phase operations
    updateBoardName: phaseOps.updateBoardName,
    updatePhaseName: phaseOps.updatePhaseName,
    addPhase: phaseOps.addPhase,
    deletePhase: phaseOps.deletePhase,
    updatePhasePositions: phaseOps.updatePhasePositions,

    // SE-5495: the rendered reason prompt. Drop it into the screen tree — the
    // move path awaits this prompt, so a screen that never mounts it would
    // leave a gated move hanging unanswered. Hook-returns-JSX matches
    // useBoardManagement's `deleteDialogComponent`.
    stageMovePromptComponent: stageMovePrompt.stageMovePromptComponent,

    // SE-5496: the Closed area dataset + reopen affordances.
    closedGroups,
    workingPhases,
    reopenCard: cardOps.reopenCard,

    // Actions - Card operations
    handlePlayerDrop: cardOps.handlePlayerDrop,
    /**
     * SE-5494: the stage move itself, exposed so a NON-DRAG surface (the player
     * preview drawer's stage selector) can move a card without re-implementing
     * the move.
     *
     * This is the whole of AC4 ("changing the stage from the drawer uses the
     * same move logic as drag, with identical reason-prompt behaviour"): drag
     * reaches this function via `handlePlayerDrop`, the drawer calls it
     * directly, and BOTH inherit the single `requestStageMoveDecision` injected
     * at the `useKanbanCardOperations` call site above. There is exactly one
     * `useStageMovePrompt` instance on the screen — mounting a second one would
     * double-render the modal, which is precisely the "separate code path" AC4
     * forbids.
     */
    movePlayerToPhase: cardOps.movePlayerToPhase,
    updateCardPositions: cardOps.reorderCardsInPhase,
    removePlayerFromBoard: cardOps.removePlayerFromBoard,
    clearError: () => setError(null),

    // Re-issue all board queries — used by the loading-timeout retry so a
    // stuck/never-resolving load can actually recover (SE-5237).
    refetch: queries.refetchAll,

    // Pagination
    loadMoreCards: queries.loadMoreCards,
  };
};
